import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

// A keyword token rendered as a small inline code chip, matching the mockup.
function KeywordCode({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="code"
      sx={{
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        color: 'primary.main',
        borderRadius: 0.75,
        px: 0.75,
        py: '1px',
        fontSize: '0.82em',
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      }}
    >
      {children}
    </Box>
  );
}

interface FileUploaderProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
}

const ACCEPTED_EXTS = ['pdf', 'docx', 'txt'];

function filterAccepted(files: FileList | File[]): File[] {
  return Array.from(files).filter((f) => {
    const ext = f.name.split('.').pop()?.toLowerCase();
    return ACCEPTED_EXTS.includes(ext || '');
  });
}

export default function FileUploader({
  onFilesSelected,
  disabled,
}: FileUploaderProps) {
  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList) return;
      const files = filterAccepted(fileList);
      if (files.length > 0) onFilesSelected(files);
      // Reset so picking the same path twice still fires onChange
      e.target.value = '';
    },
    [onFilesSelected],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const files = filterAccepted(e.dataTransfer.files);
      if (files.length > 0) onFilesSelected(files);
    },
    [onFilesSelected],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  return (
    <Paper
      variant="outlined"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      sx={{
        p: 4,
        textAlign: 'center',
        border: '2px dashed',
        borderColor: dragActive ? 'primary.dark' : 'primary.main',
        bgcolor: dragActive ? 'action.selected' : 'action.hover',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.15s',
      }}
    >
      {/* Hidden inputs — files mode and folder mode */}
      <input
        ref={filesInputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.txt"
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        // @ts-ignore — webkitdirectory is non-standard but widely supported
        webkitdirectory=""
        directory=""
        onChange={handleChange}
        style={{ display: 'none' }}
      />

      <CloudUploadIcon
        sx={{ fontSize: 48, color: 'primary.main', mb: 1 }}
      />
      <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
        Drop the job description + résumés here
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add the whole folder — one job description and all the résumés — in a
        single drop.
      </Typography>

      {/* High-visibility instruction: which file is treated as the JD. */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.4,
          alignItems: 'flex-start',
          textAlign: 'left',
          maxWidth: 580,
          mx: 'auto',
          mb: 2.5,
          px: 1.75,
          py: 1.5,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
          border: 1,
          borderColor: (t) => alpha(t.palette.primary.main, 0.2),
          borderLeft: '4px solid',
          borderLeftColor: 'primary.main',
          borderRadius: 1.25,
        }}
      >
        <DescriptionOutlinedIcon
          sx={{ color: 'primary.main', fontSize: 20, mt: '1px', flex: 'none' }}
        />
        <Typography variant="body2" color="text.secondary">
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Include the job description as one of the files.
          </Box>{' '}
          Put <KeywordCode>job</KeywordCode>, <KeywordCode>JD</KeywordCode>,{' '}
          <KeywordCode>description</KeywordCode>, or{' '}
          <KeywordCode>requirements</KeywordCode> in its filename and it's
          detected automatically — every other file is read as a résumé.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<InsertDriveFileIcon />}
          disabled={disabled}
          onClick={() => filesInputRef.current?.click()}
        >
          Choose Files
        </Button>
        <Button
          variant="outlined"
          startIcon={<FolderOpenIcon />}
          disabled={disabled}
          onClick={() => folderInputRef.current?.click()}
        >
          Choose Folder
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        <Chip label="PDF" size="small" />
        <Chip label="DOCX" size="small" />
        <Chip label="TXT" size="small" />
      </Box>
    </Paper>
  );
}
